import { Component, Input, OnInit, ViewChild } from '@angular/core'
import { UntypedFormBuilder, Validators } from '@angular/forms'
import { Subscription } from 'rxjs'
import { finalize, map, switchMap } from 'rxjs/operators'
import { Application } from 'src/app/account/services/application.service'
import { UnconfiguredS3BucketReplicationIssue } from 'src/app/account/services/issue'
import {
  AzureBlobContainerConfig,
  S3BucketConfig,
  S3ObjectReplicationMode,
  SyncPair,
} from 'src/app/account/services/sync-pair.service'
import { ApiService } from 'src/app/services/api.service'
import { ResourceConfigDto, SyncPairIssueScopeDto } from 'src/app/services/dtos'
import { UiService } from 'src/app/services/ui.service'
import { ModalDialogComponent } from 'src/app/shared/modal-dialog/modal-dialog.component'
import { Http404Error } from 'src/utils/errors'
import { handleArpioError } from 'src/utils/handle-error'
import { ViewableIssue } from '../../issues-view/issues-view.component'
import { AccountService } from '../../../services/account.service'

@Component({
  selector: 'unconfigured-azure-blob-container-issue',
  templateUrl: './unconfigured-blob-container-issue.component.html',
  styleUrls: ['./unconfigured-blob-container-issue.component.scss'],
  standalone: false,
})
export class UnconfiguredBlobContainerIssueComponent implements OnInit {
  @Input() issue: ViewableIssue = null
  @Input('sync-pair') syncPair: SyncPair = null
  @Input('application') application: Application = null

  private _value: number = null
  private loadingSubscription: Subscription = null
  private savingSubscription: Subscription = null
  private saved = false
  private config: AzureBlobContainerConfig = null
  constructor(
    public uiService: UiService,
    public apiService: ApiService,
    private formBuilder: UntypedFormBuilder,
    public accountService: AccountService
  ) {}

  ngOnInit(): void {
    let getSyncPairResourceConfig$ = !!this.syncPair
      ? this.syncPair.getSyncPairResourceConfig$(this.issue.arn)
      : this.application.getSyncPairResourceConfig$(this.issue.arn)
    this.loadingSubscription = getSyncPairResourceConfig$
      .pipe(finalize(() => (this.loadingSubscription = null)))
      .subscribe(
        (resourceConfig) => {
          if (!!resourceConfig) {
            this.config = resourceConfig.resourceConfig as S3BucketConfig
            if (this.config) {
              this.configFormModel.setValue({
                backfillRange: Math.max(
                  this.backfillOptions.findIndex((e) => e.val == this.config.backfillDays),
                  0
                ),
                objectReplicationMode: this.config.objectReplicationMode,
              })
            }
          }
        },
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )
  }

  backfillOptions = [
    { val: 0, text: 'None' },
    { val: 90, text: 'Past 90 days' },
    { val: 180, text: 'Past 180 days' },
    { val: 365, text: 'Past year' },
    { val: 365 * 2, text: 'Past 2 years' },
    { val: 365 * 3, text: 'Past 3 years' },
    { val: 365 * 4, text: 'Past 4 years' },
    { val: 365 * 5, text: 'Past 5 years' },
    { val: -1, text: 'All objects' },
  ]

  configFormModel = this.formBuilder.group({
    backfillRange: [(this.backfillOptions.length - 1).toString()],
    objectReplicationMode: [null, Validators.required],
  })

  saveClick() {
    this.saveConfig()
  }

  dialogContinueClick() {
    this.saveConfig()
  }

  dialogCancelClick() {}

  saveConfig() {
    let config = {
      resourceConfigType: 'azureBlobContainer',
      backfillDays: this.backfillRange,
      objectReplicationMode: this.objectReplicationMode,
    } as ResourceConfigDto

    let saver$ = null
    if (this.syncPair) {
      saver$ = this.syncPair.setSyncPairResourceConfig$(this.issue.arn, config)
    } else {
      saver$ = this.application.setSyncPairResourceConfig$(this.issue.arn, config)
    }
    this.savingSubscription = saver$
      .pipe(
        switchMap((_config) => this.issue.deleter$()),
        finalize(() => (this.savingSubscription = null))
      )
      .subscribe(
        (_response) => {
          this.saved = true
          this.disableControls()
        },
        (err) => {
          handleArpioError(window.location.href, this.apiService, this.uiService)(err)
        }
      )
  }

  get backfillRange() {
    if (this.objectReplicationMode === S3ObjectReplicationMode.realTime) {
      return this.backfillOptions[this.configFormModel.value.backfillRange].val
    } else {
      return -1
    }
  }

  get objectReplicationMode() {
    return this.configFormModel.value.objectReplicationMode
  }

  get snapshotModeEnabled() {
    // Not yet supported in Azure
    return false
  }

  get backfillRangeText() {
    return this.backfillOptions[this.configFormModel.value.backfillRange].text
  }

  get saving() {
    return !!this.savingSubscription && !this.savingSubscription.closed
  }

  get saveStatus() {
    if (this.saving) {
      return 'saving'
    }
    if (this.saved) {
      return 'saved'
    }
    return 'unsaved'
  }

  disableControls() {
    this.configFormModel.disable()
  }

  get validConfig() {
    return !this.configFormModel.invalid
  }
}
